import React from "react";
import { AbsoluteFill } from "remotion";
import { STEP_PHONE } from "../look";
import { type Stage, useStage } from "../stage";
import { PT_WIDTH } from "./AppScreens";
import { Phone, SCREEN_RATIO, screenWidthOf } from "./Phone";
import { StepLabel } from "./bits";

/** Device height per px of device width (bezel + screen, see `Phone`). */
const PHONE_H_PER_W = 0.07 + 0.93 * SCREEN_RATIO;

/** Tall formats: the phone grows so the app screen reads full-screen. */
const TALL_PHONE_W = 640;
const TALL_LABEL_H = 230;
const TALL_GAP = 40;

/** Wide format: label column on the left, phone on the right. */
const WIDE_PHONE_W = 440;
const WIDE_PHONE_CX = 1300;

export type StepGeom = {
  /** Device box, page px. */
  phoneW: number;
  phoneH: number;
  phoneX: number;
  phoneY: number;
  /** Screen size and top-left corner (inside the bezel), page px. */
  SW: number;
  SH: number;
  SCREEN_X: number;
  SCREEN_Y: number;
  /** Screen points → screen px (the app screens are authored at 393pt). */
  PT: number;
  /** `phoneW / 420`: scales square-authored offsets around the phone. */
  k: number;
  label: { left: number; top: number; width: number; height: number; stacked: boolean };
};

const geom = (stage: Stage): StepGeom => {
  let phoneW: number;
  let phoneX: number;
  let phoneY: number;
  let label: StepGeom["label"];

  if (stage.shape === "wide") {
    phoneW = WIDE_PHONE_W;
    phoneX = WIDE_PHONE_CX - phoneW / 2;
    phoneY = (stage.h - phoneW * PHONE_H_PER_W) / 2;
    label = { left: 170, top: 0, width: phoneX - 170 - 110, height: stage.h, stacked: true };
  } else if (stage.shape === "tall") {
    phoneW = TALL_PHONE_W;
    const group = TALL_LABEL_H + TALL_GAP + phoneW * PHONE_H_PER_W;
    const top = Math.max(stage.safeTop - 40, (stage.h - group) / 2);
    phoneX = (stage.w - phoneW) / 2;
    phoneY = top + TALL_LABEL_H + TALL_GAP;
    label = { left: 60, top, width: stage.w - 120, height: TALL_LABEL_H, stacked: false };
  } else {
    // Square master: label on top, phone centred, bleeding off the bottom.
    phoneW = STEP_PHONE.width;
    phoneX = (stage.w - phoneW) / 2;
    phoneY = STEP_PHONE.top;
    label = { left: 70, top: 58, width: stage.w - 140, height: STEP_PHONE.top - 58 - 24, stacked: false };
  }

  const SW = screenWidthOf(phoneW);
  const bezel = phoneW * 0.035;
  return {
    phoneW,
    phoneH: phoneW * PHONE_H_PER_W,
    phoneX,
    phoneY,
    SW,
    SH: SW * SCREEN_RATIO,
    SCREEN_X: phoneX + bezel,
    SCREEN_Y: phoneY + bezel,
    PT: SW / PT_WIDTH,
    k: phoneW / STEP_PHONE.width,
    label,
  };
};

export const useStepGeom = (): StepGeom => geom(useStage());

type Props = {
  background: string;
  step: number;
  label: string;
  sub?: string;
  labelStart?: number;
  dark?: boolean;
  /** Extra phone offset (px) — used for the step-1 entrance only. */
  phoneOffsetY?: number;
  screen: React.ReactNode;
  /** Page-space overlays (taps, callouts), drawn above the phone. */
  children?: React.ReactNode;
};

/**
 * Layout for steps 1-3. Square: numbered label on top, one phone centred
 * and slightly bleeding off the bottom edge (reads bigger in a small
 * embed). Tall: same, with a bigger, fully visible phone. Wide: label
 * stacked in a left column, phone on the right.
 */
export const StepStage: React.FC<Props> = ({
  background,
  step,
  label,
  sub,
  labelStart = 2,
  dark,
  phoneOffsetY = 0,
  screen,
  children,
}) => {
  const g = useStepGeom();
  return (
    <AbsoluteFill style={{ background }}>
      <div
        style={{
          position: "absolute",
          top: g.label.top,
          left: g.label.left,
          width: g.label.width,
          height: g.label.height,
          display: "flex",
          alignItems: "center",
          justifyContent: g.label.stacked ? "flex-start" : "center",
        }}
      >
        <StepLabel
          n={step}
          text={label}
          sub={sub}
          start={labelStart}
          dark={dark}
          stacked={g.label.stacked}
        />
      </div>
      <div
        style={{
          position: "absolute",
          top: g.phoneY,
          left: g.phoneX,
          translate: `0 ${phoneOffsetY}px`,
        }}
      >
        <Phone width={g.phoneW}>{screen}</Phone>
      </div>
      {children}
    </AbsoluteFill>
  );
};
